/**
 * useCart — internal hook the CartContext uses to talk to the gateway.
 *
 * SoC: this file owns the wire AND the optimistic-update policy. The
 * CartContext owns state + sharing across the SPA. UI components consume
 * the context, never this hook directly — keeps a single source of truth
 * and a single fetcher.
 *
 * Kind-aware (Kiran 2026-06-06, 0102): /shop and /build hold separate
 * carts. The active `kind` is supplied by CartContext (derived from the
 * current route — `/build/*` → 'build', everywhere else → 'shop'). Every
 * wire call sends `kind`; switching surfaces triggers a refetch so the
 * UI always shows the surface the visitor is on.
 *
 * Mutation policy:
 *   - addItem        — awaited for BOTH /shop and /build. Returns
 *                      `{ ok }` so the calling button can show the
 *                      server's actual rejection message (per-product
 *                      cap, tenant cart-value cap, etc.). Cart-level
 *                      error state is also set on failure for any other
 *                      surface that watches it. Switched from fire-and-
 *                      forget 2026-06-08 — the previous shape swallowed
 *                      server rejections so customers thought the add
 *                      succeeded. The `kind` field on the wire tells the
 *                      server which surface to write to (per-product cap
 *                      is enforced
 *                      for 'shop', skipped for 'build').
 *   - setItemQuantity — OPTIMISTIC + FIRE-AND-FORGET. UI updates the line
 *                      + totals synchronously and that's the last render;
 *                      the PATCH is debounced ~250ms (spam of +/+/+/−
 *                      ships ONE call with the final quantity) and on
 *                      success we DO NOT overwrite local state — the UI
 *                      already shows the truth, a second setCart would
 *                      just re-render for no visible change. Only the
 *                      error path re-syncs (so a server reject can't
 *                      strand the cart in a wrong optimistic state).
 *   - removeItem     — OPTIMISTIC + FIRE-AND-FORGET. Line disappears
 *                      instantly; DELETE fires immediately (a remove is
 *                      discrete, not a sweep). Same success/error
 *                      asymmetry as setItemQuantity.
 *   - clear          — sync (one-shot "empty everything", the spinner is
 *                      the right UX here).
 *   - refresh        — sync (it IS the truth-fetch).
 *
 * The optimistic recomputation lives in a single helper so the wire
 * shape stays in one place (line_total = unit_price × quantity;
 * subtotal = Σ priced line_totals; item_count = Σ quantities).
 */

import { useCallback, useEffect, useRef, useState } from 'react'
import { APIBASE } from '../constants/api.constants'
import { APIROUTES, fillPath } from '../constants/routes.constants'
import type { CartDto, CartLineDto } from '../models/cart.model'
import { isNetworkError, SERVER_UNAVAILABLE_MESSAGE } from '../utils/api/server-unavailable'

export type CartKind = 'shop' | 'build'

const EMPTY_CART: CartDto = {
  items: [],
  item_count: 0,
  distinct_lines: 0,
  subtotal_paisa: 0
}

const DEBOUNCE_MS = 250

interface JsonEnvelope<T> {
  readonly success: boolean
  readonly data?: T
  readonly error?: { readonly message?: string; readonly field?: string | null }
}

async function fetchJson<T>(path: string, init: RequestInit): Promise<T> {
  let res: Response
  try {
    res = await fetch(`${APIBASE}${path}`, { ...init, credentials: 'include' })
  } catch (err) {
    if (isNetworkError(err)) throw new Error(SERVER_UNAVAILABLE_MESSAGE)
    throw err
  }
  // 5xx → gateway / worker unreachable or crashed. Don't leak status.
  if (res.status >= 500) throw new Error(SERVER_UNAVAILABLE_MESSAGE)
  let env: JsonEnvelope<T>
  try {
    env = (await res.json()) as JsonEnvelope<T>
  } catch {
    // CF HTML error page, edge timeout, etc.
    throw new Error(SERVER_UNAVAILABLE_MESSAGE)
  }
  if (!res.ok || env.success !== true || env.data === undefined) {
    // 4xx with envelope → relay the server-supplied domain error
    // (e.g. "quantity exceeds the per-order cap"). Otherwise generic.
    const msg = env.error?.message
    throw new Error(typeof msg === 'string' && msg.length > 0 ? msg : SERVER_UNAVAILABLE_MESSAGE)
  }
  return env.data
}

function withKind(path: string, kind: CartKind): string {
  const sep = path.includes('?') ? '&' : '?'
  return `${path}${sep}kind=${encodeURIComponent(kind)}`
}

/** Recompute totals for a cart whose lines have just been mutated locally. */
function recompute(items: CartLineDto[]): CartDto {
  const item_count = items.reduce((acc, l) => acc + l.quantity, 0)
  const subtotal_paisa = items.reduce((acc, l) => acc + (l.line_total_paisa ?? 0), 0)
  return {
    items,
    item_count,
    distinct_lines: items.length,
    subtotal_paisa
  }
}

function applyQuantity(cart: CartDto, variantId: string, quantity: number): CartDto {
  const items = cart.items.map((l) =>
    l.variant_id === variantId
      ? {
          ...l,
          quantity,
          line_total_paisa: l.unit_price_paisa === null ? null : l.unit_price_paisa * quantity
        }
      : l
  )
  return recompute(items)
}

function applyRemove(cart: CartDto, variantId: string): CartDto {
  return recompute(cart.items.filter((l) => l.variant_id !== variantId))
}

/** Server response shape from POST /orders/confirm. */
export interface ConfirmOrderResult {
  readonly order_id: string
  readonly subtotal_paisa: number
  readonly item_count: number
  readonly placed_at: string
}

/** Server response shape from POST /build/requests/confirm. No money
 *  fields — build is a selection-to-publish flow, not a purchase. */
export interface ConfirmBuildRequestResult {
  readonly build_request_id: string
  readonly item_count: number
  readonly requested_at: string
}

export interface UseCart {
  readonly kind: CartKind
  readonly cart: CartDto
  readonly isLoading: boolean
  readonly error: string | null
  readonly refresh: () => Promise<void>
  /** Returns the server's response so the calling button can show
   *  "Added" vs the actual rejection message (per-product cap,
   *  tenant cart-value cap, etc.). Cart-level error state is also
   *  set on failure for any other surface that watches it. */
  readonly addItem: (
    variantId: string,
    quantity: number
  ) => Promise<{ ok: true } | { ok: false; message: string }>
  readonly setItemQuantity: (variantId: string, quantity: number) => Promise<void>
  readonly removeItem: (variantId: string) => Promise<void>
  readonly clear: () => Promise<void>
  /** Confirm the current cart into an order. Server-side: atomic
   *  insert orders + items + teardown of the shop cart. On success the
   *  local cart resets to empty. Always operates on the SHOP surface —
   *  /build's "confirm" is a future publish-sub-domain flow. */
  readonly confirmOrder: () => Promise<ConfirmOrderResult | null>
  /** Submit the current build cart as a request. Server-side: atomic
   *  insert build_requests + build_request_items + teardown of the
   *  build lines. On success the local cart resets to empty. Only
   *  meaningful when this hook's kind === 'build'. */
  readonly confirmBuildRequest: () => Promise<ConfirmBuildRequestResult | null>
  /** Customer "Email me a quote" — server renders a PDF of the current
   *  /shop cart, emails it to the signed-in customer + BCCs the operator
   *  (preference STORE.OPERATOREMAIL). No checkout, no cart teardown.
   *  Kiran 2026-06-28. */
  readonly requestQuoteEmail: () => Promise<{ ok: true; sent_to: string } | { ok: false; message: string }>
}

export function useCart(kind: CartKind): UseCart {
  const [cart, setCart] = useState<CartDto>(EMPTY_CART)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  // Per-variant debounce timers for setItemQuantity. Map (not single
  // ref) so mutating two lines in parallel doesn't cancel each other.
  const pendingTimers = useRef<Map<string, number>>(new Map())

  const run = useCallback(async (op: () => Promise<CartDto>): Promise<void> => {
    setIsLoading(true)
    setError(null)
    try {
      setCart(await op())
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Cart operation failed')
    } finally {
      setIsLoading(false)
    }
  }, [])

  const refresh = useCallback(async (): Promise<void> => {
    await run(() => fetchJson<CartDto>(withKind(APIROUTES.CART, kind), { method: 'GET' }))
  }, [run, kind])

  // Re-sync from the server on optimistic-call failure so the user never
  // sees a stuck-incorrect cart. Independent of `run` so it doesn't
  // toggle the boot spinner mid-interaction.
  const resyncSilent = useCallback(async (): Promise<void> => {
    try {
      const fresh = await fetchJson<CartDto>(withKind(APIROUTES.CART, kind), { method: 'GET' })
      setCart(fresh)
    } catch {
      // If even the re-sync fails, leave the error banner in place; the
      // user can hit "Try again".
    }
  }, [kind])

  // Surface switch (shop ↔ build) — drop stale state and refetch so the
  // visitor sees the cart for the surface they're now on.
  useEffect(() => {
    setCart(EMPTY_CART)
    void refresh()
  }, [kind, refresh])

  const addItem = useCallback(
    async (
      variantId: string,
      quantity: number
    ): Promise<{ ok: true } | { ok: false; message: string }> => {
      // Awaited (no longer fire-and-forget — Kiran 2026-06-08): the
      // server-side rejection messages for the per-product cap and the
      // tenant cart-value cap MUST reach the user, and a swallow-and-
      // resync pattern left customers thinking the add succeeded when
      // it didn't. The latency is one cart-handler round-trip, which is
      // sub-second on prod. Cart-level error is still set on failure so
      // any other surface that watches it (the cart drawer, list page)
      // gets the message too.
      setError(null)
      try {
        const next = await fetchJson<CartDto>(APIROUTES.CARTITEMS, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ variant_id: variantId, quantity, kind })
        })
        setCart(next)
        return { ok: true }
      } catch (e) {
        const message = e instanceof Error ? e.message : 'Could not add to cart'
        setError(message)
        await resyncSilent()
        return { ok: false, message }
      }
    },
    [kind, resyncSilent]
  )

  const setItemQuantity = useCallback(
    async (variantId: string, quantity: number): Promise<void> => {
      // 1. Optimistic — UI feels instant.
      setError(null)
      setCart((prev) => applyQuantity(prev, variantId, quantity))

      // 2. Debounce the network. The last quantity within DEBOUNCE_MS
      //    wins; cancels any timer already scheduled for THIS variant.
      const existing = pendingTimers.current.get(variantId)
      if (existing !== undefined) window.clearTimeout(existing)

      const timer = window.setTimeout(() => {
        pendingTimers.current.delete(variantId)
        void (async () => {
          try {
            const path = fillPath(APIROUTES.CARTITEM, { variantId })
            await fetchJson<CartDto>(path, {
              method: 'PATCH',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({ quantity, kind })
            })
            // Success → no setCart. The UI is already correct from the
            // optimistic update; a second setCart would re-render for
            // no visible change.
          } catch (e) {
            setError(e instanceof Error ? e.message : 'Could not update quantity')
            await resyncSilent()
          }
        })()
      }, DEBOUNCE_MS)
      pendingTimers.current.set(variantId, timer)
    },
    [kind, resyncSilent]
  )

  const removeItem = useCallback(
    async (variantId: string): Promise<void> => {
      // Cancel any pending qty PATCH for this variant — a remove
      // supersedes whatever the stepper was about to ship.
      const pending = pendingTimers.current.get(variantId)
      if (pending !== undefined) {
        window.clearTimeout(pending)
        pendingTimers.current.delete(variantId)
      }
      setError(null)
      setCart((prev) => applyRemove(prev, variantId))

      try {
        const path = withKind(fillPath(APIROUTES.CARTITEM, { variantId }), kind)
        await fetchJson<CartDto>(path, { method: 'DELETE' })
        // Success → no setCart; the optimistic remove already shows truth.
      } catch (e) {
        setError(e instanceof Error ? e.message : 'Could not remove line')
        await resyncSilent()
      }
    },
    [kind, resyncSilent]
  )

  const clear = useCallback(async (): Promise<void> => {
    // Cancel every pending PATCH — clear wins.
    for (const t of pendingTimers.current.values()) window.clearTimeout(t)
    pendingTimers.current.clear()
    await run(() => fetchJson<CartDto>(withKind(APIROUTES.CART, kind), { method: 'DELETE' }))
  }, [run, kind])

  const confirmOrder = useCallback(async (): Promise<ConfirmOrderResult | null> => {
    // Cancel any in-flight qty PATCHes — confirm wins.
    for (const t of pendingTimers.current.values()) window.clearTimeout(t)
    pendingTimers.current.clear()
    setIsLoading(true)
    setError(null)
    try {
      const result = await fetchJson<ConfirmOrderResult>(APIROUTES.ORDERSCONFIRM, { method: 'POST' })
      // Server tore down the shop lines; local view drops to empty.
      setCart(EMPTY_CART)
      return result
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not place order')
      return null
    } finally {
      setIsLoading(false)
    }
  }, [])

  const confirmBuildRequest = useCallback(async (): Promise<ConfirmBuildRequestResult | null> => {
    // Cancel any in-flight qty PATCHes — request wins.
    for (const t of pendingTimers.current.values()) window.clearTimeout(t)
    pendingTimers.current.clear()
    setIsLoading(true)
    setError(null)
    try {
      const result = await fetchJson<ConfirmBuildRequestResult>(APIROUTES.BUILDREQUESTSCONFIRM, { method: 'POST' })
      // Server tore down the build lines; local view drops to empty.
      setCart(EMPTY_CART)
      return result
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not submit build request')
      return null
    } finally {
      setIsLoading(false)
    }
  }, [])

  const requestQuoteEmail = useCallback(
    async (): Promise<{ ok: true; sent_to: string } | { ok: false; message: string }> => {
      try {
        const result = await fetchJson<{ ok: true; sent_to: string }>(
          APIROUTES.CARTQUOTEEMAIL,
          { method: 'POST' }
        )
        return result
      } catch (e) {
        return { ok: false, message: e instanceof Error ? e.message : 'Could not send the quote' }
      }
    },
    []
  )

  return { kind, cart, isLoading, error, refresh, addItem, setItemQuantity, removeItem, clear, confirmOrder, confirmBuildRequest, requestQuoteEmail }
}
